import styled, { css, type RuleSet } from 'styled-components'
import {
	darkOrange,
	orange,
	blue,
	blueDark,
	danger,
	dangerHover,
	textMuted,
	white,
	warmTint,
} from '@/theme/colors'
import { radii } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { actionButton } from '@/theme/recipes'

export type ButtonVariant = 'primary' | 'secondary' | 'tonal' | 'ghost' | 'danger' | 'cool' | 'icon'
export type ButtonSize = 'sm' | 'md' | 'lg'
export type ButtonShape = 'pill'

export interface ButtonStyleProps {
	/** What the button means; defaults to `primary`. */
	variant?: ButtonVariant
	/** Control height; defaults to `md`. `icon` ignores it. */
	size?: ButtonSize
	/** `pill` for the one chip that sits beside round avatars; otherwise the control radius. */
	shape?: ButtonShape
}

const variants: Record<ButtonVariant, RuleSet<object>> = {
	/** The one action that moves things forward: Save, Add, Confirm. */
	primary: css`
		background: ${orange};
		color: ${white};

		&:hover:not(:disabled) {
			background: ${darkOrange};
		}
	`,
	/** Sits beside a primary: Cancel, Edit, Filter, Export. */
	secondary: css`
		background: ${white};
		color: ${darkOrange};
		border-color: ${warmTint};

		&:hover:not(:disabled) {
			background: ${warmTint};
		}
	`,
	/** The soft fill from the create-trip handoff: warm tint ground, dark orange text. Copy controls, "Go to trip". */
	tonal: css`
		background: ${warmTint};
		color: ${darkOrange};

		&:hover:not(:disabled) {
			filter: brightness(0.97);
		}
	`,
	/** Header and toolbar actions on a tinted ground, and inline actions: Clear, Back, Show more. */
	ghost: css`
		background: transparent;
		color: ${darkOrange};

		&:hover:not(:disabled) {
			background: ${warmTint};
		}
	`,
	/** Destructive: Delete, Remove, Leave trip. */
	danger: css`
		background: ${danger};
		color: ${white};

		&:hover:not(:disabled) {
			background: ${dangerHover};
		}
	`,
	/** The blue CTA: the create-trip Where step's Add, the public site's Get Orbit. */
	cool: css`
		background: ${blue};
		color: ${white};

		&:hover:not(:disabled) {
			background: ${blueDark};
		}
	`,
	/** A 36px square holding a single glyph: close, remove, more. */
	icon: css`
		width: 36px;
		height: 36px;
		padding: 0;
		border-radius: ${radii.small};
		background: transparent;
		color: ${textMuted};

		&:hover:not(:disabled) {
			background: ${warmTint};
			color: ${darkOrange};
		}
	`,
}

const sizes: Record<ButtonSize, RuleSet<object>> = {
	sm: css`
		padding: 7px 14px;
		font-size: ${fontSizes.caption};
		border-radius: ${radii.small};
	`,
	md: css``,
	lg: css`
		padding: 14px 24px;
		font-size: ${fontSizes.body};
	`,
}

/**
 * The app's one button. Built on the theme's `actionButton` recipe (Lato 700,
 * 12px control radius, color shift on hover, press on active), with the intent
 * carried by `variant`, the height by `size` and the one pill chip by `shape`. Renders as anything through
 * styled-components' `as` — a `Link`, an anchor — and keeps `variant` / `size`
 * and `shape` off the DOM.
 */
export const Button = styled.button.withConfig({
	shouldForwardProp: prop => prop !== 'variant' && prop !== 'size' && prop !== 'shape',
})<ButtonStyleProps>`
	${actionButton}
	border: 1px solid transparent;
	white-space: nowrap;
	text-decoration: none;
	${({ size = 'md' }) => sizes[size]}
	${({ variant = 'primary' }) => variants[variant]}
	${({ shape }) => shape === 'pill' && `border-radius: ${radii.pill};`}

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`
